/**
 * <DocumentsList> — reusable list + upload component.
 *
 * Used standalone in the global Documents Vault page AND inline as a tab
 * under tenant profile + property detail screens. Caller passes either
 * `propertyId` or `tenantId` (mutually exclusive) and the component handles
 * fetch, upload, download, and delete.
 *
 * Props:
 *   scope: 'property' | 'tenant' | 'all'   ('all' for the global vault page)
 *   propertyId, tenantId   (for scoped views)
 *   propertyName, tenantName  (display only)
 *   hideHeader (bool)  — for tenant profile embed
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Image as ImageIcon, Download, Trash2, Lock, Clock,
  Plus, Search, ShieldAlert,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import {
  CATEGORY_LABELS, SENSITIVE_CATEGORIES, formatBytes,
} from '../../utils/documentsHelpers';
import DocumentUploadModal from './DocumentUploadModal';
import { track } from '../../services/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isExpired = (d) => d && new Date(d) <= new Date();
const isExpiringSoon = (d) => {
  if (!d) return false;
  const days = Math.floor((new Date(d) - new Date()) / 86400000);
  return days >= 0 && days <= 30;
};

const DocumentsList = ({
  scope = 'all',
  propertyId, tenantId,
  propertyName, tenantName,
  hideHeader = false,
}) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (propertyId) params.property_id = propertyId;
      if (tenantId) params.tenant_id = tenantId;
      const res = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDocs(res.data || []);
    } catch (err) {
      toast({
        title: 'Could not load documents',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [token, propertyId, tenantId, toast]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const onDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const a = document.createElement('a');
      a.href = res.data.data_uri;
      a.download = res.data.file_name || doc.title;
      document.body.appendChild(a); a.click(); a.remove();
      track('document_downloaded', { category: doc.category });
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    }
  };

  const onDelete = async (doc) => {
    // Sensitive docs (Aadhaar/PAN) get an extra-strict confirm prompt
    const sensitive = SENSITIVE_CATEGORIES.has(doc.category);
    const verb = sensitive ? 'permanently delete this Aadhaar/PAN/KYC document' : 'delete this document';
    if (!window.confirm(`Are you sure you want to ${verb}?\n\n"${doc.title}"\n\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Deleted', description: `${doc.title} removed from vault.` });
      track('document_deleted', { category: doc.category, was_sensitive: sensitive });
      fetchDocs();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    }
  };

  // Filter — search across title, category label, tenant/property name
  const filtered = docs.filter((d) => {
    if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (d.title || '').toLowerCase().includes(q) ||
      (CATEGORY_LABELS[d.category] || '').toLowerCase().includes(q) ||
      (d.tenant_name || '').toLowerCase().includes(q) ||
      (d.property_name || '').toLowerCase().includes(q)
    );
  });

  const uniqueCategories = Array.from(new Set(docs.map(d => d.category)));

  return (
    <div data-testid="documents-list">
      {!hideHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {scope === 'tenant'   && `KYC, agreement, and supporting docs for ${tenantName || 'this tenant'}.`}
              {scope === 'property' && `Lease, FSSAI, fire NOC, and other docs for ${propertyName || 'this property'}.`}
              {scope === 'all'      && 'All documents across your properties and tenants.'}
            </p>
          </div>
          {scope !== 'all' && (
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="documents-upload-btn"
            >
              <Plus size={16} className="mr-1" /> Upload
            </Button>
          )}
        </div>
      )}

      {/* Search + filter */}
      {docs.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, category, tenant, or property…"
              className="pl-9"
              data-testid="documents-search"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            data-testid="documents-category-filter"
          >
            <option value="all">All categories</option>
            {uniqueCategories.map(c => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
        </div>
      )}

      {/* States */}
      {loading && (
        <div className="text-center py-12 text-gray-500" data-testid="documents-loading">Loading…</div>
      )}

      {!loading && docs.length === 0 && (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200" data-testid="documents-empty">
          <FileText className="mx-auto text-gray-400 mb-3" size={36} />
          <p className="font-semibold text-gray-700 mb-1">No documents yet</p>
          <p className="text-sm text-gray-500 mb-4">
            {scope === 'tenant'   && 'Upload Aadhaar, PAN, photo, and rent agreement to keep KYC ready.'}
            {scope === 'property' && 'Upload lease deed, FSSAI, fire NOC, GST cert, and other property docs.'}
            {scope === 'all'      && 'No documents have been uploaded yet for any property or tenant.'}
          </p>
          {scope !== 'all' && (
            <Button onClick={() => setShowUpload(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus size={14} className="mr-1" /> Upload first document
            </Button>
          )}
        </div>
      )}

      {!loading && filtered.length === 0 && docs.length > 0 && (
        <p className="text-center text-gray-500 py-8">No documents match your search.</p>
      )}

      {/* Grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="documents-grid">
          {filtered.map((d) => {
            const isImg = (d.mime_type || '').startsWith('image/');
            const sensitive = SENSITIVE_CATEGORIES.has(d.category);
            const expired = isExpired(d.expiry_date);
            const expiring = !expired && isExpiringSoon(d.expiry_date);
            return (
              <div
                key={d.id}
                className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all p-4"
                data-testid={`document-card-${d.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isImg ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                    {isImg ? <ImageIcon size={18} /> : <FileText size={18} />}
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    {sensitive && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                        <Lock size={9} /> Encrypted
                      </span>
                    )}
                    {expired && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 ring-1 ring-red-200">
                        <ShieldAlert size={9} /> Expired
                      </span>
                    )}
                    {expiring && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                        <Clock size={9} /> Expiring
                      </span>
                    )}
                  </div>
                </div>

                <p className="font-semibold text-gray-900 text-sm leading-snug mb-1 line-clamp-2">{d.title}</p>
                <p className="text-xs text-gray-500 mb-2">
                  {CATEGORY_LABELS[d.category] || d.category} · {formatBytes(d.file_size)}
                </p>
                {scope === 'all' && (d.tenant_name || d.property_name) && (
                  <p className="text-[11px] text-gray-500 italic mb-2">
                    {d.tenant_name ? `Tenant: ${d.tenant_name}` : `Property: ${d.property_name}`}
                  </p>
                )}
                {d.expiry_date && (
                  <p className={`text-[11px] mb-2 ${expired ? 'text-red-600 font-semibold' : expiring ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>
                    Expires {new Date(d.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                )}
                {d.notes && (
                  <p className="text-[11px] text-gray-500 italic line-clamp-1 mb-2">{d.notes}</p>
                )}
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDownload(d)}
                    className="flex-1 h-8 text-xs"
                    data-testid={`document-download-${d.id}`}
                  >
                    <Download size={12} className="mr-1" /> Download
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(d)}
                    className="h-8 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                    data-testid={`document-delete-${d.id}`}
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload modal — only when this list is scoped (we know what to attach to) */}
      {scope !== 'all' && (
        <DocumentUploadModal
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onUploaded={fetchDocs}
          scope={scope}
          propertyId={propertyId}
          tenantId={tenantId}
          propertyName={propertyName}
          tenantName={tenantName}
        />
      )}
    </div>
  );
};

export default DocumentsList;
